<template>
	<div class="schedules-container pa-3 overflow-y-auto">
		<div
			class="d-flex ma-n1"
			:class="{ 'flex-column': $vuetify.breakpoint.mdAndDown }"
			style="height: calc(100% + 8px)">
			<div class="pa-1" :style="{ width: $vuetify.breakpoint.lgAndUp ? '406px' : '' }">
				<v-card>
					<v-data-table
						:headers="headers"
						:items="schedulesByLocationId"
						v-model="selectedSchedules"
						:search="appBarSearch"
						show-select
						dense
						:class="{ 'small-footer': $vuetify.breakpoint.md }"
						:height="pageHeight(151)"
						:footer-props="{ 'items-per-page-text': '' }"
						:loading="loadingSchedules"
						:items-per-page="-1"
						:item-class="(item) => getRowsForDataTable(schedulesByLocationId.indexOf(item))">
						<template #[`header.data-table-select`]>
							<v-btn
								icon
								@click.stop="
									selectedSchedules =
										selectedSchedules.length === schedulesByLocationId.length ? [] : schedulesByLocationId.slice()
								"
								style="width: 30px">
								<v-icon
									:color="selectedSchedules.length === schedulesByLocationId.length ? 'primary' : 'grey'"
									size="22px">
									{{ selectedToggleIcon }}
								</v-icon>
							</v-btn>
						</template>
						<template #item="{ item, index, select, isSelected }">
							<v-tooltip right>
								<template #activator="{ on, attrs }">
									<tr
										:class="getRowsForDataTable(index)"
										@click="
											selectedSchedule = item;
											scheduleDialog = true;
										"
										v-bind="attrs"
										v-on="on">
										<td class="text-left px-2">
											<v-btn icon @click.stop="select(!isSelected)" style="width: 30px">
												<v-icon :color="isSelected ? 'primary' : 'grey'" size="22px">
													{{ isSelected ? 'mdi-eye-outline' : 'mdi-eye-off-outline' }}
												</v-icon>
											</v-btn>
										</td>
										<td class="text-left px-2">
											{{ item.scheduleRuleName }}
										</td>
										<td class="text-right pl-2 pr-0">
											<div
												v-if="item.scheduleOccurrences && item.scheduleOccurrences.length"
												class="occurrence-dots-inline">
												<span
													v-for="(color, idx) in item.scheduleOccurrences.map((occ) => occ.color)"
													:key="idx"
													:class="['occurrence-dot', `pos-${idx}`]"
													:style="{ backgroundColor: color }" />
											</div>
										</td>
										<td
											class="px-2 text-right"
											:style="
												schedulesByLocationId.some((schedule) => schedule.scheduleTasksDetail?.length === 0)
													? 'width: 70px'
													: 'width: 41px'
											">
											<v-icon
												v-if="item.scheduleTasksDetail.length === 0"
												class="cursor-pointer"
												color="primary"
												@click.stop="
													showDeleteDialog = true;
													scheduleToDelete = item.id;
												"
												size="25">
												mdi-delete
											</v-icon>
											<v-icon class="cursor-pointer" color="primary" @click.stop="editSchedule(item)" size="25">
												mdi-pencil
											</v-icon>
										</td>
									</tr>
								</template>
								<div style="max-width: 30vw" class="truncate-text text-left">
									{{ $t('duration') }}: {{ formatTimestamp(item.scheduleStartDate) }} -
									{{ formatTimestamp(item.scheduleEndDate) }}
									<br />
									<span v-if="item.scheduleRuleDescription">
										{{ $t('description') }}: {{ item.scheduleRuleDescription }}
									</span>
								</div>
							</v-tooltip>
						</template>
					</v-data-table>
				</v-card>
			</div>
			<div class="pa-1 flex-grow-1 flex-shrink-0">
				<v-card style="height: 100%">
					<v-card-title class="d-flex flex-row flex-wrap justify-space-between align-center">
						<div class="d-flex">
							<v-btn
								elevation="0"
								tile
								@click="calendarView = 'month'"
								class="white--text"
								:color="calendarView === 'month' ? 'primary' : 'primary2'"
								style="border-radius: 4px 0 0 4px; height: 40px">
								{{ $t('month') }}
							</v-btn>
							<v-btn
								elevation="0"
								tile
								@click="calendarView = 'week'"
								class="white--text"
								:class="calendarView === 'week' ? 'primaryBg' : 'primary2Bg'"
								style="border-left: 1px solid #000; border-right: 1px solid #000; height: 40px">
								{{ $t('week') }}
							</v-btn>
							<v-btn
								elevation="0"
								tile
								@click="calendarView = 'day'"
								:color="calendarView === 'day' ? 'primary' : 'primary2'"
								class="white--text"
								style="border-radius: 0 4px 4px 0; height: 40px">
								{{ $t('day') }}
							</v-btn>
						</div>
						<div>
							<h3>{{ getMonthName(currentMonth) }} {{ currentYear }}</h3>
						</div>
						<div>
							<v-btn color="primary2" @click="goPrev" class="white--text" style="height: 40px">
								<v-icon>mdi-arrow-left</v-icon>{{ $t('previous') }}
							</v-btn>
							<v-btn color="primary2" @click="goToday" class="white--text" style="height: 40px">
								{{ $t('today') }}
							</v-btn>
							<v-btn color="primary2" @click="goNext" class="white--text" style="height: 40px">
								{{ $t('next') }}<v-icon>mdi-arrow-right</v-icon>
							</v-btn>
						</div>
					</v-card-title>
					<v-card-text style="height: calc(100vh - 211px)">
						<v-calendar
							v-model="currentDate"
							:type="calendarView"
							:events="filteredCalendarEvents"
							:event-color="(e) => e.color"
							:event-text-color="(e) => e.textColor"
							event-overlap-mode="column"
							:locale="this.$i18n.locale === 'en' ? 'en' : 'es'"
							@click:event="onEventClick"
							@click:more="viewDate"
							@click:date="viewDate"
							:weekdays="[1, 2, 3, 4, 5, 6, 0]">
							<template #event="{ event }">
								<v-tooltip
									:color="!event.isSuccess && !event.isError ? 'light' : 'primary'"
									left
									content-class="pa-1 full-opacity-tooltip">
									<template #activator="{ on }">
										<div
											:class="{ 'disabled-day': isPastEvent(event) }"
											v-on="on"
											class="d-flex justify-center text-center hp-100"
											style="flex-wrap: wrap">
											{{ event.name }}
											<span
												v-if="event.hasTasks.length > 0"
												style="
													position: absolute;
													right: 0;
													background-color: #a51717;
													width: 25px;
													border-left: 1px solid #333;
												"
												class="d-flex justify-center hp-100">
												<label class="font-weight-bold px-1 text-white">
													{{ event.hasTasks.length > 9 ? '9+' : event.hasTasks.length }}
												</label>
											</span>
										</div>
									</template>
									<TaskListTooltip :event="event" />
								</v-tooltip>
							</template>
							<template #day-label-header="{ day, date }">
								<div class="text-center">
									<div>{{ day }} {{ monthName(date) }}</div>
								</div>
							</template>
						</v-calendar>
					</v-card-text>
				</v-card>
			</div>
		</div>
		<ScheduleDetailsDialog
			:selectedSchedule="selectedSchedule"
			:showDialog="scheduleDialog"
			@update:showDialog="(val) => (scheduleDialog = val)" />
		<TaskListDetailsDialog
			:selectedEventData="selectedEventData"
			:showDialog="eventDialog"
			@update:showDialog="(val) => (eventDialog = val)" />
		<DeleteDialog :dialog.sync="showDeleteDialog" @confirm="deleteSchedule(scheduleToDelete)" />
	</div>
</template>

<script>
import { RRule } from 'rrule';
import { mapActions, mapGetters, mapState } from 'vuex';
import {
	getBrowserTimeZone,
	getRowsForDataTable,
	monthName,
	getMonthName,
	formatTimestamp,
} from '../../../utils/utilities';
import TaskListDetailsDialog from '../../dialogs/TaskListDetailsDialog.vue';
import TaskListTooltip from './TaskListTooltip.vue';
import DeleteDialog from '../../dialogs/DeleteDialog.vue';
import ScheduleDetailsDialog from '../../dialogs/ScheduleDetailsDialog.vue';

export default {
	components: { ScheduleDetailsDialog, TaskListDetailsDialog, TaskListTooltip, DeleteDialog },
	data () {
		return {
			currentDate: new Date().toISOString().substring(0, 10),
			currentMonth: -1,
			currentYear: 0,
			calendarEvents: [],
			calendarView: 'week',
			loadingSchedules: false,
			scheduleDialog: false,
			selectedSchedule: null,
			eventDialog: false,
			selectedEventData: null,
			selectedSchedules: [],
			showDeleteDialog: false,
			scheduleToDelete: null,
		};
	},
	computed: {
		...mapState({
			authenticated: (state) => state.authentication.userData,
			schedulesByLocationId: (state) =>
				state.schedules.schedulesByLocationId.sort((a, b) => {
					const nameA = a.scheduleRuleName.toLowerCase();
					const nameB = b.scheduleRuleName.toLowerCase();
					if (nameA < nameB) return -1;
					if (nameA > nameB) return 1;
					return 0;
				}),
		}),
		...mapGetters({
			pageHeight: 'pageHeight',
			appBarSearch: 'filterbar/getSearch',
			appBarSearchTaskList: 'filterbar/getSearchTaskList',
		}),
		headers () {
			return [
				{ text: '', value: 'data-table-select', sortable: false, class: 'px-2' },
				{ text: this.$t('name'), value: 'scheduleRuleName', class: 'px-2' },
				{ text: '', value: 'dots', sortable: false, class: 'px-2' },
				{ text: '', value: 'actions', sortable: false, class: 'px-2' },
			];
		},
		selectedToggleIcon () {
			return this.selectedSchedules.length === this.schedulesByLocationId.length
				? 'mdi-eye-outline'
				: this.selectedSchedules.length > 0 && this.selectedSchedules.length < this.schedulesByLocationId.length
				? 'mdi-radiobox-indeterminate-variant'
				: 'mdi-eye-off-outline';
		},
		filteredCalendarEvents () {
			if (!this.appBarSearchTaskList || this.appBarSearchTaskList.trim() === '') {
				return this.calendarEvents;
			}
			const searchTerm = this.appBarSearchTaskList.toLowerCase().trim();
			return this.calendarEvents.filter(
				(event) =>
					event.name.toLowerCase().includes(searchTerm) ||
					event.hasTasks.some((task) => {
						return task.taskDetailDescription.toLowerCase().includes(searchTerm);
					}),
			);
		},
		weekdayOptions () {
			const weekdayOptions = [
				{ text: this.$t('monday'), value: RRule.MO },
				{ text: this.$t('tuesday'), value: RRule.TU },
				{ text: this.$t('wednesday'), value: RRule.WE },
				{ text: this.$t('thursday'), value: RRule.TH },
				{ text: this.$t('friday'), value: RRule.FR },
				{ text: this.$t('saturday'), value: RRule.SA },
				{ text: this.$t('sunday'), value: RRule.SU },
			];
			return weekdayOptions;
		},
	},
	async mounted () {
		this.loadSchedulesByLocationId();
		this.currentMonth = new Date(this.currentDate).getMonth() + 1;
		this.currentYear = new Date(this.currentDate).getFullYear();
	},
	methods: {
		getMonthName,
		getBrowserTimeZone,
		monthName,
		formatTimestamp,
		...mapActions({
			getSchedulesByLocationId: 'schedules/getSchedulesByLocationId',
			deleteScheduleById: 'schedules/deleteScheduleById',
		}),
		getRowsForDataTable,
		async loadSchedulesByLocationId () {
			this.loadingSchedules = true;
			const selectedHospitalAssignedId = JSON.parse(localStorage.getItem('selectedHospitalAssignedId'));
			await this.getSchedulesByLocationId(selectedHospitalAssignedId).then((res) => {});
			this.loadingSchedules = false;
		},
		// Calendar methods
		convertBetweenTimeZones (dateTimeString, fromTimeZone, toTimeZone) {
			// 🕐 Step 1: Parse the input datetime string into a Date.
			// This gives a timestamp in the local environment timezone.
			const dateInSource = new Date(dateTimeString);

			// Create locale options for interpreting this date as being in the source timezone.
			const sourceOptions = { timeZone: fromTimeZone };

			// Convert it to a localized representation according to the source timezone.
			// The goal is to reconstruct what the date would look like in that zone.
			const sourceDate = new Date(dateInSource.toLocaleString('en-US', sourceOptions));

			// 🕑 Step 2: Find the difference in milliseconds between parsed date and source interpretation.
			// This essentially gives the offset between your runtime timezone and the source zone.
			const diffMs = dateInSource.getTime() - sourceDate.getTime();

			// Apply that difference to compute the corresponding UTC timestamp.
			const utcDate = new Date(dateInSource.getTime() + diffMs);

			// 🕒 Step 3: Format the UTC instant as seen from the target timezone.
			const targetOptions = {
				timeZone: toTimeZone,
				year: 'numeric',
				month: '2-digit',
				day: '2-digit',
				hour: '2-digit',
				minute: '2-digit',
				second: '2-digit',
				hour12: false, // use 24-hour format
			};

			const formatter = new Intl.DateTimeFormat('en-US', targetOptions);
			const parts = formatter.formatToParts(utcDate);

			// Helper to extract each part (year, month, day, etc.) by type.
			const get = (type) => parts.find((p) => p.type === type)?.value;

			// 🕓 Step 4: Build ISO-like string (without 'Z' because it’s now local in target zone).
			return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}:${get('second')}`;
		},
		goPrev () {
			const base = new Date(this.currentDate);
			if (this.calendarView === 'month') base.setMonth(base.getMonth() - 1);
			else if (this.calendarView === 'week') base.setDate(base.getDate() - 7);
			else base.setDate(base.getDate() - 1);
			this.currentDate = base.toISOString().substr(0, 10);
			this.currentMonth = new Date(base).getMonth() + 1;
			this.currentYear = new Date(base).getFullYear();
		},
		goNext () {
			const base = new Date(this.currentDate);
			if (this.calendarView === 'month') base.setMonth(base.getMonth() + 1);
			else if (this.calendarView === 'week') base.setDate(base.getDate() + 7);
			else base.setDate(base.getDate() + 1);
			this.currentDate = base.toISOString().substr(0, 10);
			this.currentMonth = new Date(base).getMonth() + 1;
			this.currentYear = new Date(base).getFullYear();
		},
		goToday () {
			const base = new Date();
			this.currentDate = base.toISOString().substr(0, 10);
			this.currentMonth = new Date(base).getMonth() + 1;
			this.currentYear = new Date(base).getFullYear();
		},

		async generateEventsFromSchedules (schedules) {
			this.calendarEvents = [];
			const colorsMapping = schedules.reduce((acc, schedule) => {
				schedule.scheduleOccurrences.forEach((occurrence) => {
					const baseColor = occurrence.color || '#1976D2'; // Default to a blue if none provided
					const textColor = occurrence.textColor || '#FFFFFF'; // Default text color
					acc[occurrence.id] = {
						color: baseColor,
						textColor: textColor,
					};
				});
				return acc;
			}, {});
			const browserTimeZone = this.getBrowserTimeZone();

			// Cache for converted time zones to avoid redundant calculations
			const timeZoneCache = new Map();
			const convertCached = (dateTime, fromTimeZone, toTimeZone) => {
				const key = `${dateTime}-${fromTimeZone}-${toTimeZone}`;
				if (!timeZoneCache.has(key)) {
					timeZoneCache.set(key, this.convertBetweenTimeZones(dateTime, fromTimeZone, toTimeZone));
				}
				return timeZoneCache.get(key);
			};

			// First pass: Generate events from RRule occurrences
			const generatedEventTimes = new Set(); // Track generated events to avoid duplicates

			schedules.forEach((schedule) => {
				if (!schedule.scheduleOccurrences?.length) return;

				schedule.scheduleOccurrences.forEach((occurrence) => {
					// Build RRule options from occurrence
					const rruleOptions = {
						freq: RRule[occurrence.frequency],
						dtstart: new Date(occurrence.occurrenceStartAt),
						until: new Date(occurrence.occurrenceEndAt),
						interval: occurrence.interval || 1,
						wkst: RRule.MO,
					};

					// Add optional RRule parameters
					if (occurrence.count) rruleOptions.count = occurrence.count;
					if (occurrence.byWeekDay?.length) {
						rruleOptions.byweekday = occurrence.byWeekDay.map((d) => this.weekdayOptions[d].value);
					}
					if (occurrence.byMonth?.length) rruleOptions.bymonth = occurrence.byMonth;
					if (occurrence.byMonthDay?.length) rruleOptions.bymonthday = occurrence.byMonthDay;
					if (occurrence.byYearDay?.length) rruleOptions.byyearday = occurrence.byYearDay;
					if (occurrence.byWeekNumber?.length) rruleOptions.byweekno = occurrence.byWeekNumber;
					if (occurrence.byHour?.length) rruleOptions.byhour = occurrence.byHour;
					if (occurrence.byMinute?.length) rruleOptions.byminute = occurrence.byMinute;

					// Generate all occurrence dates
					const rule = new RRule(rruleOptions);
					const dates = rule.all();

					// Pre-filter tasks for this occurrence
					const occurrenceTasks = schedule.scheduleTasksDetail.filter((task) => task.occurrenceId === occurrence.id);

					dates.forEach((date) => {
						const timezoneOffsetMs = date.getTimezoneOffset() * 60000;
						const start = new Date(date.getTime() + timezoneOffsetMs);
						const end = new Date(start.getTime() + Number(occurrence.occurrenceDuration) * 60000);

						// Create unique key for this event time
						const eventKey = `${schedule.id}-${occurrence.id}-${start.getTime()}`;
						generatedEventTimes.add(eventKey);

						// Find matching tasks for this specific event time
						const hasTask = occurrenceTasks.filter((task) => {
							const taskStartLocal = convertCached(task.plannedStartAt, task.taskTimezoneId, browserTimeZone);
							const occurrenceLocalDate = convertCached(date, browserTimeZone, browserTimeZone);
							return taskStartLocal === occurrenceLocalDate;
						});

						this.calendarEvents.push({
							scheduleId: schedule.id,
							name: occurrence.occurrenceName,
							description: occurrence.occurrenceDescription,
							start,
							end,
							color: colorsMapping[occurrence.id].color,
							textColor: colorsMapping[occurrence.id].textColor,
							timed: true,
							locationNames: schedule.scheduleLocations?.map((l) => l.taskLocationName).join(', ') || '',
							executors: schedule.scheduleExecutors?.map((e) => e.taskExecutorUserName).join(', ') || '',
							occurrence,
							schedule,
							hasTasks: hasTask,
						});
					});
				});
			});

			// Second pass: Add standalone tasks that weren't generated by RRule
			schedules.forEach((schedule) => {
				schedule.scheduleTasksDetail.forEach((taskDetail) => {
					const occurrence = schedule.scheduleOccurrences.find((occ) => occ.id === taskDetail.occurrenceId);
					if (!occurrence) return;

					const plannedStartAt = new Date(taskDetail.plannedStartAt);
					const timezoneOffsetMs = plannedStartAt.getTimezoneOffset() * 60 * 1000;
					const start = new Date(plannedStartAt.getTime() + timezoneOffsetMs);

					// Check if this event was already generated by RRule
					const eventKey = `${schedule.id}-${occurrence.id}-${start.getTime()}`;
					if (generatedEventTimes.has(eventKey)) return;

					const end = new Date(start.getTime() + Number(occurrence.occurrenceDuration) * 60 * 1000);

					// Find matching tasks for this specific event time
					const hasTask = schedule.scheduleTasksDetail.filter((task) => {
						if (occurrence.id !== task.occurrenceId) return false;
						const taskStartLocal = convertCached(task.plannedStartAt, task.taskTimezoneId, browserTimeZone);
						const occurrenceLocalDate = convertCached(plannedStartAt, browserTimeZone, browserTimeZone);
						return taskStartLocal === occurrenceLocalDate;
					});

					this.calendarEvents.push({
						scheduleId: schedule.id,
						name: occurrence.occurrenceName,
						description: occurrence.occurrenceDescription,
						start,
						end,
						color: colorsMapping[occurrence.id].color,
						textColor: colorsMapping[occurrence.id].textColor,
						timed: true,
						locationNames: schedule.scheduleLocations?.map((l) => l.taskLocationName).join(', ') || '',
						executors: schedule.scheduleExecutors?.map((e) => e.taskExecutorUserName).join(', ') || '',
						occurrence,
						schedule,
						hasTasks: hasTask,
					});
				});
			});
		},

		viewDate ({ date }) {
			this.calendarView = 'day';
			this.currentDate = date;
			this.currentMonth = new Date(date).getMonth() + 1;
			this.currentYear = new Date(date).getFullYear();
		},
		onEventClick ({ event }) {
			const today = new Date();
			today.setHours(0, 0, 0, 0);

			const eventDate = new Date(event.start);
			if (eventDate < today) {
				// Ignore past events
				return;
			}
			// Find the full val object (schedule) this event belongs to
			const foundSchedule = this.schedulesByLocationId?.find((s) =>
				s.scheduleOccurrences.some((o) => o.id === event.occurrenceId),
			);

			// Store it and show dialog
			this.selectedEventData = foundSchedule || event;
			this.eventDialog = true;
		},
		editSchedule (item) {
			this.$store.commit('SET_UPDATE_DETAILS_DRAWER', {
				updateDrawer: true,
				page2: 'schedules',
				item: item,
			});
		},
		async deleteSchedule (scheduleItem) {
			// Call the API wrapper (deleteScheduleById), which is assumed to return { data: { resFlag: boolean } }
			const deleteResponse = await this.deleteScheduleById(scheduleItem);

			// If deletion succeeded (resFlag truthy), reload schedules by current location id
			if (deleteResponse.data.resFlag) {
				this.loadSchedulesByLocationId();
			}
		},
		isPastEvent (event) {
			const today = new Date();
			today.setHours(0, 0, 0, 0);
			const eventDate = new Date(event.start);
			return eventDate < today;
		},
	},
	watch: {
		selectedSchedules (val) {
			this.generateEventsFromSchedules(val);
		},
		schedulesByLocationId: {
			immediate: false,
			deep: true,
			async handler (value) {
				let schedulesToReFillCalendar = [];
				if (this.selectedSchedules.length > 0) {
					const selectedSchedules = this.selectedSchedules.map((s) => s.id);
					schedulesToReFillCalendar = this.schedulesByLocationId.filter((s) => selectedSchedules.includes(s.id));
				}
				if (schedulesToReFillCalendar.length !== 0) {
					await this.generateEventsFromSchedules(schedulesToReFillCalendar);
				}
				if (this.eventDialog && this.selectedEventData) {
					const scheduleId = this.selectedEventData?.schedule?.id ?? this.selectedEventData?.id;
					if (scheduleId != null) {
						let updatedEventData = null;
						if (this.selectedEventData?.occurrence && this.selectedEventData?.start) {
							const prevOccId = this.selectedEventData.occurrence.id;
							const prevStartTime = new Date(this.selectedEventData.start).getTime();
							updatedEventData = this.filteredCalendarEvents.find((e) => {
								try {
									return (
										e.schedule?.id === scheduleId &&
										e.occurrence?.id === prevOccId &&
										new Date(e.start).getTime() === prevStartTime
									);
								} catch (err) {
									return false;
								}
							});
						}
						// Fallback: pick any event for the schedule if an exact match wasn't found
						if (!updatedEventData) {
							updatedEventData = this.filteredCalendarEvents.find((e) => e.schedule?.id === scheduleId);
						}
						if (updatedEventData) {
							this.selectedEventData = updatedEventData;
						} else {
							// As last resort, set the schedule object (useful for editing schedule-level details)
							const updatedSchedule = this.schedulesByLocationId.find((s) => s.id === scheduleId);
							if (updatedSchedule) this.selectedEventData = updatedSchedule;
						}
					}
				}
			},
		},
	},
};
</script>

<style lang="scss" scoped>
.schedules-container {
	height: calc(100vh - 115px) !important;
	border-top: 1px solid #ccc;
	overflow: hidden;
}
.primary2Bg {
	background-color: #a41717 !important;
}
.primaryBg {
	background-color: #ff585f !important;
}
.disabled-day {
	color: rgba(0, 0, 0, 0.5); /* grayed out */
	opacity: 0.8;
	background-color: rgb(255, 255, 255);
	cursor: not-allowed;
}
.small-footer :deep(.v-input--hide-details) {
	margin-left: 0;
}
.small-footer :deep(.v-data-footer__pagination) {
	margin: 0 8px 0 8px;
}
.small-footer :deep(.v-btn--icon) {
	margin: 0 !important;
}

.occurrence-dots-inline {
	display: inline-flex;
	flex-direction: column;
	height: 100%;
	flex-wrap: wrap-reverse;
	align-items: center;
	justify-content: center;
	gap: 4px;
}
.occurrence-dot {
	width: 10px;
	height: 10px;
	border-radius: 50%;
	display: block;
}
</style>
